import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  PROMPT_EDIT_MODELS,
  PromptEditModel,
} from "domains/assets/constants/promptEdit";
import {
  PROMPT_EDIT_DEFAULT_FORM,
  UseAssetPromptEditReturnValue,
} from "domains/assets/hooks/useAssetPromptEdit";
import { extractFirstQueryParam } from "domains/commons/misc";
import useRouter from "domains/navigation/hooks/useRouter";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import {
  useLazyGetAssetsByAssetIdQuery,
  useLazyGetJobIdQuery,
  usePostAssetGetBulkMutation,
} from "infra/api/generated/api";
import _ from "lodash";

interface UseImportParamsPromptEditArg {
  setForm: UseAssetPromptEditReturnValue["setForm"];
  setReference: UseAssetPromptEditReturnValue["setReference"];
  onValueChange?: (key: string) => void;
}

export default function useImportParamsPromptEdit({
  setForm,
  setReference,
  onValueChange,
}: UseImportParamsPromptEditArg) {
  const router = useRouter();
  const { selectedProject } = useTeamContext();
  const [getAssetById] = useLazyGetAssetsByAssetIdQuery();
  const [getAssetsByIds] = usePostAssetGetBulkMutation();
  const [getJobById] = useLazyGetJobIdQuery();

  const urlParamsRef = useRef<{
    importAssetId?: string;
    importPromptFromAssetId?: string;
    restartPromptEdit?: string;
  }>({});
  const urlParams = useMemo(
    () =>
      router.isReady
        ? {
            importAssetId: extractFirstQueryParam(router.query.importAssetId),
            importPromptFromAssetId: extractFirstQueryParam(
              router.query.importPromptFromAssetId
            ),
            restartPromptEdit: extractFirstQueryParam(
              router.query.restartPromptEdit
            ),
          }
        : {},
    [
      router.isReady,
      router.query.importAssetId,
      router.query.importPromptFromAssetId,
      router.query.restartPromptEdit,
    ]
  );

  // ----------------------------------

  const importAssetFromId = useCallback(
    async (assetId: string) => {
      const { data: assetData } = await getAssetById({
        projectId: selectedProject.id,
        assetId,
      });

      if (!assetData?.asset) {
        return;
      }

      const importedAsset = assetData.asset;
      await setReference(importedAsset);
    },
    [getAssetById, selectedProject.id, setReference]
  );

  const importPromptFromAssetId = useCallback(
    async (assetId: string) => {
      try {
        const { data: assetData } = await getAssetById({
          projectId: selectedProject.id,
          assetId,
        });
        if (!assetData?.asset) throw new Error("NOT_FOUND");

        setForm((form) => ({
          ...form,
          prompt: assetData.asset.metadata.prompt ?? "",
          negativePrompt: assetData.asset.metadata.negativePrompt ?? "",
        }));
      } catch (err: any) {
        if (err.message === "NOT_FOUND") return;
        throw err;
      }
    },
    [getAssetById, selectedProject.id, setForm]
  );

  const restartPromptEdit = useCallback(
    async (assetId: string) => {
      const { data: assetData } = await getAssetById({
        projectId: selectedProject.id,
        assetId,
      });

      if (!assetData?.asset || !assetData.asset?.metadata.parentJobId) {
        return;
      }

      const { data: jobData } = await getJobById({
        projectId: selectedProject.id,
        jobId: assetData.asset?.metadata.parentJobId,
      });
      if (!jobData?.job) {
        return;
      }

      const importedAsset = assetData.asset;
      const jobInput = jobData?.job?.metadata?.input as any;

      const parentId = importedAsset.metadata.parentId;
      const referenceImagesIds = jobInput.referenceImages ?? [];

      const refAssetIds = _.compact([parentId, ...referenceImagesIds]);
      if (refAssetIds.length === 0) {
        return;
      }

      const refAssets = await getAssetsByIds({
        projectId: selectedProject.id,
        body: {
          assetIds: refAssetIds,
        },
      }).unwrap();

      const parentImage = parentId
        ? refAssets.assets.find((asset) => asset.id === parentId)
        : undefined;
      const referenceImages = referenceImagesIds.length
        ? refAssets.assets.filter((asset) =>
            referenceImagesIds.includes(asset.id)
          )
        : [];

      if (!parentImage) {
        return;
      }

      setReference(parentImage);

      const newModelId = (
        PROMPT_EDIT_MODELS.includes(jobInput.modelId ?? jobInput.model) // model is deprecated but still used in old jobs
          ? jobInput.modelId ?? jobInput.model
          : PROMPT_EDIT_DEFAULT_FORM.modelId
      ) as PromptEditModel;

      setForm((form) => ({
        ...form,
        prompt: importedAsset.metadata.prompt ?? "",
        modelId: newModelId,
        refImages: referenceImages,
        ...(newModelId === "gpt-image-1"
          ? {
              aspectRatio:
                jobInput.aspectRatio ?? PROMPT_EDIT_DEFAULT_FORM.aspectRatio,
              quality: jobInput.quality ?? PROMPT_EDIT_DEFAULT_FORM.quality,
            }
          : {}),
      }));

      if (importedAsset.metadata.prompt) {
        onValueChange?.("prompt");
      }
      if (referenceImages.length) {
        onValueChange?.("refImages");
      }
    },
    [
      getAssetById,
      getAssetsByIds,
      getJobById,
      onValueChange,
      selectedProject.id,
      setForm,
      setReference,
    ]
  );

  // ----------------------------------

  useEffect(() => {
    if (_.isEqual(urlParams, urlParamsRef.current)) {
      return;
    }

    urlParamsRef.current = urlParams;

    if (
      urlParams.importAssetId ||
      urlParams.importPromptFromAssetId ||
      urlParams.restartPromptEdit
    ) {
      void router.replace({
        pathname: router.pathname,
        query: _.omit(
          router.query,
          "importAssetId",
          "importPromptFromAssetId",
          "restartPromptEdit"
        ),
      });
    }

    if (urlParams.importAssetId) {
      void importAssetFromId(urlParams.importAssetId);
    } else if (urlParams.importPromptFromAssetId) {
      void importPromptFromAssetId(urlParams.importPromptFromAssetId);
    } else if (urlParams.restartPromptEdit) {
      void restartPromptEdit(urlParams.restartPromptEdit || "");
    }
  }, [
    importAssetFromId,
    importPromptFromAssetId,
    restartPromptEdit,
    router,
    urlParams,
  ]);
}
